import { forwardRef, useRef, useCallback } from 'react';
import { Box, FormHelperText } from '@mui/material';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { matchIsNumeric } from 'src/utils';

type OtpFieldProps = {
    error?: string;
    value: string;
    name: string;
    onComplete: () => void;
    onChange: (value: string) => void;
};

const OtpField = forwardRef(({ name, value, onComplete, onChange, error = '', ...otherProps }: OtpFieldProps, ref) => {
    const prevValueRef = useRef<string>('');
    const isPasteRef = useRef<boolean>(false);
    // Флаг для предотвращения двойной отправки
    const isSubmittingRef = useRef<boolean>(false);

    // Обертка для onComplete с защитой от двойного вызова
    const safeOnComplete = useCallback(() => {
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;
        onComplete();
        // Сбрасываем флаг через 2 секунды (время на обработку запроса)
        setTimeout(() => {
            isSubmittingRef.current = false;
        }, 2000);
    }, [onComplete]);

    const handleChange = (newValue: string) => {
        // Проверяем, если значение изменилось сразу на полную длину (6 символов),
        // это может быть вставка из буфера обмена
        const prevLength = prevValueRef.current.length;
        const newLength = newValue.length;

        // Если значение изменилось сразу на полную длину, это вставка
        if (prevLength < 6 && newLength === 6) {
            isPasteRef.current = true;
        }

        prevValueRef.current = newValue;
        onChange(newValue);

        // Если значение достигло нужной длины, вызываем onComplete
        // Это работает как для обычного ввода, так и для вставки
        if (newLength === 6) {
            // Небольшая задержка для вставки, чтобы убедиться, что все поля заполнены
            const delay = isPasteRef.current ? 150 : 0;
            setTimeout(() => {
                safeOnComplete();
                isPasteRef.current = false;
            }, delay);
        } else if (newLength < 6) {
            isPasteRef.current = false;
            // Сбрасываем флаг отправки при изменении кода
            isSubmittingRef.current = false;
        }
    };

    // Обработчик вставки из буфера обмена
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = (e.clipboardData || (window as any).clipboardData).getData('text');

        // Оставляем только цифры и ограничиваем длину до 6 символов
        const cleanedValue = pastedText.replace(/\D/g, '').slice(0, 6);

        if (cleanedValue.length > 0) {
            isPasteRef.current = true;
            handleChange(cleanedValue);
        }
    };

    return (
        <Box>
            <MuiOtpInput
                maxWidth={320}
                ref={ref}
                // maxWidth={270}
                gap={'8px'}
                mb={1}
                length={6}
                value={value}
                validateChar={matchIsNumeric}
                onChange={handleChange}
                // Убираем onComplete чтобы избежать двойной отправки - используем только handleChange
                TextFieldsProps={{
                    error: !!error,
                    name: name,
                    onPaste: handlePaste,
                    ...otherProps,
                }}
            />
            {!!error && (
                <Box ml={1}>
                    <FormHelperText error>{error}</FormHelperText>
                </Box>
            )}
        </Box>
    );
});

export default OtpField;
